import { useWeatherStore } from '@/stores/weather-store';

interface WindInfoBadgeProps {
  propertyId: string;
}

/**
 * Fixed overlay badge showing current wind direction and speed.
 * Positioned absolute — must be placed inside a `position:relative` container.
 * Returns null when weather data is unavailable.
 */
export function WindInfoBadge({ propertyId }: WindInfoBadgeProps) {
  const { getConditions } = useWeatherStore();
  const conditions = getConditions(propertyId);

  if (!conditions) return null;

  // Arrow points TO where wind is going (windDeg is FROM direction)
  const arrowRotation = (conditions.windDeg + 180) % 360;
  const speedMph = Math.round(conditions.windSpeed);

  const tempF = Math.round(conditions.temp);

  return (
    <div
      className="absolute top-3 right-3 z-[1001] bg-bark-800 border border-bark-600 rounded-full px-3 py-1.5 flex items-center gap-1.5"
      aria-label={`${tempF}°F · Wind: ${conditions.windDirection} ${speedMph} mph`}
    >
      <span className="text-xs font-semibold text-bark-100" data-testid="wind-badge-temp">
        {tempF}°F
      </span>
      <span className="text-bark-500 text-xs" aria-hidden="true">·</span>
      <svg
        width="12"
        height="12"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ transform: `rotate(${arrowRotation}deg)`, flexShrink: 0 }}
      >
        <path
          d="M9 2L9 14M9 2L5 7M9 2L13 7"
          stroke="#e8e5e0"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="text-xs font-semibold text-bark-100">
        {conditions.windDirection} {speedMph} mph
      </span>
    </div>
  );
}
